import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import type { StateObject } from "../redux/types"
import type { AutoCloseMessage, LoadingStateObject, FiltersState } from "./reducer"
import { GLOBAL_FILTER_IDENTIFIER } from "./reducer"
import type { FiltersIdentifier, FiltersObject, LoadingStateType } from "./types"
import { LoadingState } from "./types"
import { defaultFilter } from "./utils"
import { areOperationsBeingProcessed } from "../operations/selectors"
import { areLocationsAuthDataLoaded, getAccounts, getLocationsAuthData, getSelectedAccount } from "../auth/selectors"
import { DEFAULT_TIMEZONE } from "../screens/reports/utils"
import { StatusFilterEnum } from "../common/filters/StatusFilterComponent"

export const getFiltersStateObject = (state: StateObject): FiltersState => state.filters

export const getLoadingStateDataObject = (state: StateObject): Record<string, LoadingStateObject> =>
    getFiltersStateObject(state).loadings

export const getFiltersStateDataObject = (state: StateObject): Record<FiltersIdentifier, FiltersObject> =>
    getFiltersStateObject(state).filters

export const getReloadButtonClickedCount = (state: StateObject): number =>
    getFiltersStateObject(state).reloadButtonClickedCount

export const getLoadingState = (state: StateObject, identifier: string): LoadingStateType => {
    const block: LoadingStateObject = getLoadingStateDataObject(state)[identifier]

    if (block) {
        if (block.error === true) {
            return LoadingState.Error
        } else if (block.count > 0) {
            return LoadingState.Loading
        } else {
            return LoadingState.Success
        }
    } else {
        return LoadingState.Success
    }
}

export const isStateLoading = (state: StateObject, identifier: string): boolean =>
    getLoadingState(state, identifier) === LoadingState.Loading

export const isStateSuccess = (state: StateObject, identifier: string): boolean =>
    getLoadingState(state, identifier) === LoadingState.Success

export const isStateError = (state: StateObject, identifier: string): boolean =>
    getLoadingState(state, identifier) === LoadingState.Error

export const anythingIsLoading = (state: StateObject): boolean => {
    const loadingObjects = getLoadingStateDataObject(state)
    return (
        Object.keys(loadingObjects).some(objKey => loadingObjects[objKey] && loadingObjects[objKey].count === 1) ||
        areOperationsBeingProcessed(state) ||
        false
    )
}

export const getFilters = (state: StateObject, identifier: FiltersIdentifier): FiltersObject =>
    getFiltersStateDataObject(state)[identifier] || defaultFilter

export const getStatusFilterValue = (state: StateObject, identifier: FiltersIdentifier): StatusFilterEnum =>
    getFilters(state, identifier).status ?? StatusFilterEnum.Active

export const getSelectedLocationIdsFromGlobalFilter = (state: StateObject): string[] => {
    const globalFilter = getFilters(state, GLOBAL_FILTER_IDENTIFIER)
    return globalFilter?.location_name ?? []
}

export const getDateFilter = (
    state: StateObject
): {
    from: string
    to: string
} => getFilters(state, GLOBAL_FILTER_IDENTIFIER).date

export const getTimestampFilter = (
    state: StateObject
): {
    from: string
    to: string
} => getFilters(state, GLOBAL_FILTER_IDENTIFIER).timestamp

export const areAnyLocationsSelected = (state: StateObject): boolean => {
    return getSelectedLocationIdsFromGlobalFilter(state).length > 0
}

export const getAutoCloseMessage = (state: StateObject): Maybe<AutoCloseMessage> => {
    return getFiltersStateObject(state).autoCloseMessage
}

export const getTimezoneForQueryParam = (state: StateObject, locationIds: string[]): string => {
    const locations = getLocationsAuthData(state).filter(l => locationIds.includes(l.id))

    if (!locations || locations.length === 0) {
        return DEFAULT_TIMEZONE
    }

    const timeZone = locations[0].timezone
    const isSameTimeZone = locations.every(l => l.timezone === timeZone)

    if (isSameTimeZone) {
        return timeZone
    } else {
        // TODO Clash of timezones, what now? Use first location for now...
        return timeZone
    }
}

export const areAccountsAndLocationsLoading = (state: StateObject) =>
    !getAccounts(state) || !getSelectedAccount(state) || !areLocationsAuthDataLoaded(state)
